import { computed, observable, action } from "mobx";
import { NodeStore, StoreType } from "./NodeStore";
import { GRID_NODE_SIZE, GRID_GAP } from "../Constants";

/**
 * This class represents a collection of nodes in a hierarchical container.
 * It extends the NodeStore class and encapsulates nesting, recursive traversal, 
 * and alternate layout modes for the contained nodes while sharing base node behavior.
 */
export class NodeCollectionStore extends NodeStore {
    @observable
    public nodes: NodeStore[] = [];

    @observable
    public isGridView: boolean = false;

    public type: StoreType = StoreType.Collection;

    public parent?: NodeCollectionStore;

    /**
     * This method computes the CSS transform for positioning the collection.
     * Child nodes use this as part of their world-space calculations.
     */
    @computed
    public get transform(): string {
        return `translate(${this.x}px, ${this.y}px)`;
    }

    /**
     * This mnethod adds one or more nodes to the collection.
     * Each added node's parent property is set to this collection.
     * @param stores The node(s) to add.
     */
    @action
    public addNodes(stores: NodeStore[]): void {
        stores.forEach(store => {
            store.parent = this;
            this.nodes.push(store);
        });
    }

    /**
     * This method removes a node from the collection along with any 
     * links to it from other nodes.
     * @param node The node to remove.
     */
    @action
    public removeNode(node: NodeStore): void {
        this.removeLinksToNode(node.Id);
        this.nodes = this.nodes.filter(n => n.Id !== node.Id);
    }

    /**
     * This private helper method removes all links to a specified node ID.
     * @param nodeId The ID of the node to remove links to.
     */
    private removeLinksToNode(nodeId: string): void {
        for (const n of this.nodes) {
            n.links = n.links.filter(id => id !== nodeId);

            if (n.type === StoreType.Collection) {
                (n as NodeCollectionStore).removeLinksToNode(nodeId);
            }
        }
    }

    /**
     * This public getter method returns the root collection in the hierarchy.
     * @returns The root collection.
     */
    public getRoot(): NodeCollectionStore {
        let current: NodeCollectionStore = this;

        while (current.parent) {
            current = current.parent;
        }

        return current;
    }

    /**
     * This method uses recursion to find a node by its ID within the collection and its sub-collections.
     * @param id The ID of the node to find.
     * @returns The ID of the node if found. Null if otherwise.
     */
    public getNodeById(id: string): NodeStore | null {
        for (const node of this.nodes) {
            if (node.Id === id) return node;

            if (node.type === StoreType.Collection) {
                const found = (node as NodeCollectionStore).getNodeById(id);
                if (found) return found;
            }
        }

        return null;
    }

    /**
     * This method applies grid sizing to all nodes in the collection recursively.
     * It stores the previous width and height of each node before resizing.
     */
    @action
    public applyGridSizing() {
        for (const node of this.nodes) {
            if (node.prevWidth == null) {
                node.prevWidth = node.width;
                node.prevHeight = node.height;
            }

            node.width = GRID_NODE_SIZE;
            node.height = GRID_NODE_SIZE;

            if (node.type === StoreType.Collection) {
                (node as NodeCollectionStore).applyGridSizing();
            }
        }
    }

    /**
     * This method restores the original sizing of all nodes in the collection recursively.
     * It uses the previously stored width and height values.
     */
    @action
    public restoreFreeformSizing() {
        for (const node of this.nodes) {
            if (node.prevWidth != null && node.prevHeight != null) {
                node.width = node.prevWidth;
                node.height = node.prevHeight;

                node.prevWidth = undefined;
                node.prevHeight = undefined;
            }

            if (node.type === StoreType.Collection) {
                (node as NodeCollectionStore).restoreFreeformSizing();
            }
        }
    }
}